import { useEffect } from 'react';

/**
 * Keeps page trees in different windows in step. The view of imported pages opens in its own window, next to the
 * ordinary tree; publishing or deleting pages there changes both lists, so the window that made the change
 * announces it and every other window showing the site reloads. BroadcastChannel reaches every window of the same
 * origin, which is all the admin's windows.
 */
const CHANNEL_NAME = 'm24-page-tree';

/** Identifies this window, so it does not reload for its own announcements: it already updated its own state. */
const WINDOW_ID = Math.random().toString(36).slice(2);

interface PageTreeChange {
  siteId: number;
  from: string;
}

const openChannel = (): BroadcastChannel | null =>
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);

export function announcePageTreeChange(siteId: number): void {
  const channel = openChannel();
  if (!channel) {
    return;
  }
  const change: PageTreeChange = { siteId, from: WINDOW_ID };
  channel.postMessage(change);
  channel.close();
}

/** Calls `onChange` whenever another window announces a change to `siteId`'s page tree. */
export function usePageTreeChanges(siteId: number, onChange: () => void): void {
  useEffect(() => {
    const channel = openChannel();
    if (!channel || !siteId) {
      return undefined;
    }
    channel.onmessage = (event: MessageEvent<PageTreeChange>) => {
      if (event.data?.siteId === siteId && event.data.from !== WINDOW_ID) {
        onChange();
      }
    };
    return () => channel.close();
  }, [siteId, onChange]);
}
